import React, { RefObject, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import EliteBadge from 'components/EliteBadge/EliteBadge';
import {
  CHECKOUT_SOURCES,
  ELITE_MOBILE_MAX_WIDTH,
  STICKY_BAR_BOTTOM_OFFSET_DESKTOP,
  STICKY_BAR_CONTENT_GAP,
} from './constants';
import './StickySubscribeBar.css';

interface StickySubscribeBarProps {
  compareGridRef: RefObject<HTMLElement | null>;
  finalCtaRef: RefObject<HTMLElement | null>;
  perMonth: string;
  /** "billed yearly" or "billed monthly"; hidden on mobile. */
  suffix: string;
  onSubscribe: (source: string) => void;
  isCheckoutLoading: boolean;
  /** Bottom padding the page should carry while the bar is visible (0 when hidden). */
  onClearanceChange: (px: number) => void;
}

const NAV_SELECTOR = '.left-navigation-bar';

const isMobile = () => typeof window !== 'undefined' && window.innerWidth <= ELITE_MOBILE_MAX_WIDTH;

/**
 * StickySubscribeBar (Asana 1219949663037427, Decision 11). Visibility comes from
 * two IntersectionObservers, never scrollY: shown once the compare grid has
 * scrolled fully above the viewport, hidden while FinalCta is in view. Centered
 * in the content area by measuring the left nav (there is no sidebar-width CSS
 * variable; the nav is 250px, 72px collapsed). Reports its clearance so the
 * page pads itself and the bar never covers an in-page button or the footer.
 */
const StickySubscribeBar: React.FC<StickySubscribeBarProps> = ({
  compareGridRef,
  finalCtaRef,
  perMonth,
  suffix,
  onSubscribe,
  isCheckoutLoading,
  onClearanceChange,
}) => {
  const [compareAbove, setCompareAbove] = useState(false);
  const [finalVisible, setFinalVisible] = useState(false);
  const [navWidth, setNavWidth] = useState(0);
  const pillRef = useRef<HTMLDivElement>(null);
  const visible = compareAbove && !finalVisible;

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    const compare = compareGridRef.current;
    const final = finalCtaRef.current;
    const compareObserver = new IntersectionObserver(([entry]) =>
      setCompareAbove(!entry.isIntersecting && entry.boundingClientRect.bottom < 0),
    );
    const finalObserver = new IntersectionObserver(([entry]) => setFinalVisible(entry.isIntersecting));
    if (compare) compareObserver.observe(compare);
    if (final) finalObserver.observe(final);
    return () => {
      compareObserver.disconnect();
      finalObserver.disconnect();
    };
  }, [compareGridRef, finalCtaRef]);

  // Measure the nav so the bar centers inside the content area on desktop.
  useEffect(() => {
    const measure = () => {
      const nav = document.querySelector<HTMLElement>(NAV_SELECTOR);
      setNavWidth(!isMobile() && nav ? nav.getBoundingClientRect().width : 0);
    };
    measure();
    window.addEventListener('resize', measure);
    const nav = document.querySelector<HTMLElement>(NAV_SELECTOR);
    let observer: ResizeObserver | null = null;
    if (nav && typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(measure);
      observer.observe(nav);
    }
    return () => {
      window.removeEventListener('resize', measure);
      observer?.disconnect();
    };
  }, []);

  // Clearance = bar height + content gap, plus the bottom offset on desktop
  // (on mobile the 16px offset is absorbed): 100px desktop, 82px mobile.
  useLayoutEffect(() => {
    if (!visible) {
      onClearanceChange(0);
      return;
    }
    const height = pillRef.current?.getBoundingClientRect().height ?? 0;
    onClearanceChange(Math.round(height + STICKY_BAR_CONTENT_GAP + (isMobile() ? 0 : STICKY_BAR_BOTTOM_OFFSET_DESKTOP)));
  }, [visible, onClearanceChange, perMonth, suffix]);

  if (!visible) return null;

  return createPortal(
    <div
      className="elite-sticky-bar"
      style={{ '--sticky-nav-offset': `${navWidth}px` } as React.CSSProperties}
      data-testid="elite-sticky-bar"
    >
      <div className="elite-sticky-bar__pill" ref={pillRef}>
        <EliteBadge isElite size={22} />
        <span className="elite-sticky-bar__text">
          Elite · {perMonth}/mo<span className="elite-sticky-bar__suffix"> {suffix}</span>
        </span>
        <button
          type="button"
          className="elite-subscribe-button elite-sticky-bar__button"
          onClick={() => onSubscribe(CHECKOUT_SOURCES.sticky)}
          disabled={isCheckoutLoading}
        >
          <span className="elite-sticky-bar__label-long">Subscribe Now</span>
          <span className="elite-sticky-bar__label-short">Subscribe</span>
        </button>
      </div>
    </div>,
    document.body,
  );
};

export default StickySubscribeBar;
